<template>
  <Transition name="overlay-fade">
    <div class="age-verify-overlay" v-if="visible">
      <div class="age-verify-modal text-black" role="dialog" aria-modal="true" aria-labelledby="age-verify-title">
        <picture>
          <source srcset="@/assets/images/logo.webp" type="image/webp" />
          <img
            src="@/assets/images/logo.png"
            alt="Logo"
            class="logo"
            width="300"
            height="142"
            decoding="async"
            loading="eager"
          />
        </picture>

        <div class="modal-content-body ">
          <h2 id="age-verify-title" class="fs-5">Verificación de Edad</h2>
          <p class="subtitle fs-7">
            Este sitio contiene contenido exclusivo para adultos.<br />
            ¿Confirmas que tenés <strong>18 años o más</strong>?
          </p>
        </div>

        <div class="btn-group-verify">
          <button @click="verify(true)" class="btn-verify btn-verify--primary">
            <i class="fas fa-check-circle"></i>
            <span>Sí, soy mayor de 18</span>
          </button>
          <button @click="verify(false)" class="btn-verify btn-verify--ghost">
            <i class="fas fa-times-circle"></i>
            <span>No, soy menor</span>
          </button>
        </div>

        <p class="legal-note text-black">
          Al ingresar aceptas nuestros
          <a href="#" @click.prevent class="fs-7">Términos y Condiciones</a>
          y confirmas que es legal acceder a este contenido en tu jurisdicción.
        </p>

      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, defineEmits } from 'vue'

const emit = defineEmits(['verified'])
const visible = ref(true)

const verify = (isAdult) => {
  if (isAdult) {
    localStorage.setItem('ageVerified', 'true')
    visible.value = false
    setTimeout(() => emit('verified'), 400)
  } else {
    window.location.href = 'https://www.google.com'
  }
}
</script>

<style scoped>
/* ── Overlay ───────────────────────────────────────────── */
.age-verify-overlay {
  position: fixed;
  inset: 0;
  background-image: url('@/assets/images/services-opacity.webp');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

/* ── Modal card ────────────────────────────────────────── */
.age-verify-modal {
  background: #ffffff;
  border-radius: 20px;
  padding: 2.5rem 2rem 2rem;
  text-align: center;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.35);
  animation: modal-pop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
}

@keyframes modal-pop {
  from {
    opacity: 0;
    transform: scale(0.88) translateY(24px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* ── +18 badge ─────────────────────────────────────────── */
.age-badge {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(45, 106, 79, 0.4);
}

.age-badge span {
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 800;
  letter-spacing: -0.5px;
}

/* ── Logo ──────────────────────────────────────────────── */
.logo {
  width: 300px;
  height: auto;
}

/* ── Text content ──────────────────────────────────────── */
.modal-content-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

h2 {
  color: var(--black-color);
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.3px;
}

.subtitle {
  color: var(--gray-500);
  line-height: 1.6;
  margin: 0;
}

.subtitle strong {
  color: var(--primary-color);
  font-weight: 700;
}

/* ── Button group ──────────────────────────────────────── */
.btn-group-verify {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  width: 100%;
}

.btn-verify {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.85rem 1.5rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.22s ease;
  letter-spacing: 0.01em;
}

.btn-verify--primary {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  color: #ffffff;
  box-shadow: 0 4px 16px rgba(45, 106, 79, 0.35);
}

.btn-verify--primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(45, 106, 79, 0.45);
  filter: brightness(1.05);
}

.btn-verify--primary:active {
  transform: translateY(0);
}

.btn-verify--ghost {
  background: transparent;
  color: var(--gray-500);
  border: 1.5px solid var(--gray-200);
}

.btn-verify--ghost:hover {
  background: var(--gray-100);
  border-color: var(--gray-300);
  color: var(--gray-700);
}

/* ── Legal note ────────────────────────────────────────── */
.legal-note {
  font-size: 0.75rem;
  line-height: 1.5;
  margin: 0;
  max-width: 320px;
}

.legal-note a {
  color: var(--primary-color);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.legal-note a:hover {
  color: var(--primary-dark);
}

/* ── Overlay transition ────────────────────────────────── */
.overlay-fade-leave-active {
  transition: opacity 0.4s ease;
}

.overlay-fade-leave-to {
  opacity: 0;
}

/* ── Mobile ────────────────────────────────────────────── */
@media (max-width: 480px) {
  .age-verify-modal {
    padding: 2rem 1.5rem 1.75rem;
    border-radius: 16px;
  }

  h2 {
    font-size: 1.25rem;
  }

  .btn-verify {
    padding: 0.78rem 1rem;
    font-size: 0.95rem;
  }
}
</style>
